/**
 * ErrorBoundary — Attrape les erreurs React (ex: insertBefore, unmount races)
 * et affiche un écran de récupération propre au lieu du crash rouge.
 *
 * Usage dans App.js :
 *   <ErrorBoundary>
 *     <BrowserRouter>...</BrowserRouter>
 *   </ErrorBoundary>
 */
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log en dev uniquement
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary]', error, errorInfo);
    }

    // Auto-recovery pour les erreurs insertBefore (Chrome translate / extensions DOM)
    // On tente un re-render automatique jusqu'à 3 fois avant d'afficher l'écran d'erreur
    const isInsertBeforeError = error?.message?.includes('insertBefore') ||
      error?.message?.includes('removeChild') ||
      error?.message?.includes('not a child');
    
    if (isInsertBeforeError && this.state.retryCount < 3) {
      console.warn(`[ErrorBoundary] insertBefore auto-recovery (tentative ${this.state.retryCount + 1}/3)`);
      setTimeout(() => {
        this.setState(prev => ({ hasError: false, error: null, errorInfo: null, retryCount: prev.retryCount + 1 }));
      }, 100);
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, retryCount: 0 });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, retryCount: 0 });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          padding: '2rem',
        }}>
          <div style={{
            background: 'white',
            borderRadius: '24px',
            padding: '3rem',
            maxWidth: '480px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#1a1a2e',
              marginBottom: '0.75rem',
            }}>
              Erreur d'affichage
            </h1>
            <p style={{
              color: '#666',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              marginBottom: '2rem',
            }}>
              Un problème technique est survenu. Vos données sont en sécurité.
              Rechargez la page pour continuer.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre style={{
                background: '#f8f8f8',
                borderRadius: '12px',
                padding: '1rem',
                fontSize: '0.75rem',
                color: '#c0392b',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: '150px',
                marginBottom: '1.5rem',
                border: '1px solid #eee',
              }}>
                {this.state.error.toString()}
                {this.state.errorInfo?.componentStack?.slice(0, 500)}
              </pre>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                onClick={this.handleReload}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '14px',
                  padding: '0.75rem 2rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseOver={(e) => { e.target.style.transform = 'scale(1.03)'; e.target.style.boxShadow = '0 4px 15px rgba(102,126,234,0.4)'; }}
                onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = 'none'; }}
              >
                🔄 Recharger
              </button>
              <button
                onClick={this.handleGoHome}
                style={{
                  background: 'transparent',
                  color: '#667eea',
                  border: '2px solid #667eea',
                  borderRadius: '14px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                onMouseOver={(e) => { e.target.style.transform = 'scale(1.03)'; }}
                onMouseOut={(e) => { e.target.style.transform = 'scale(1)'; }}
              >
                🏠 Accueil
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
